import React, { FC } from 'react';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import { DocumentData, Timestamp } from 'firebase/firestore';
import { Box, Theme, Typography, styled } from '@mui/material';
import { UserAvatar } from '../../../../shared/components';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';

export interface BhabiFirestoreModel extends DocumentData {
  message: string;
  groupID: string;
  name: string;
  avatar: string;
  createdAt: Timestamp;
  senderID: string;
  receiverID: string;
  isRead: string;
  threadID: string[];
}

const formatMessageTime = (ts?: Timestamp): string => {
  if (!ts?.seconds) return '';
  const date = new Date(ts.seconds * 1000);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const Message: FC<{
  message: BhabiFirestoreModel;
}> = ({ message }) => {
  const { firebaseUID } = useSelector((state: Store) => state.userReducer);
  const isOwn = firebaseUID === message.senderID;
  const time = formatMessageTime(message.createdAt);

  return (
    <MessageWrapper sx={isOwn ? sxRight : { alignSelf: 'flex-start' }}>
      <UserAvatar
        hideState
        photoURL={message.avatar}
        name={message.name}
        fetchCurrentUser={isOwn}
        avatarProps={{
          sx: {
            ...sxLeft(),
            width: 25,
            height: 25,
          },
        }}
      />
      <Box className="flex flex-col min-w-0 flex-1">
        <UserName>{message.name}</UserName>
        <UserMessage>{message.message}</UserMessage>

        {/* Bottom-right meta strip: light/muted time + read receipt (modern chat style) */}
        <Box
          className="flex items-center justify-end gap-1 mt-1 -mb-0.5 select-none"
          sx={{ opacity: 0.65 }}
        >
          {time && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                lineHeight: 1,
                color: theme =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(236, 239, 244, 0.55)'
                    : 'rgba(46, 52, 64, 0.55)',
                letterSpacing: '0.2px',
              }}
            >
              {time}
            </Typography>
          )}
          {isOwn && (
            message.isRead === 'true' ? (
              <DoneAllIcon
                sx={{
                  fontSize: '0.85rem',
                  color: theme => theme.palette.success.light,
                }}
              />
            ) : (
              <DoneIcon
                sx={{
                  fontSize: '0.85rem',
                  color: theme =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(236, 239, 244, 0.5)'
                      : 'rgba(46, 52, 64, 0.45)',
                }}
              />
            )
          )}
        </Box>
      </Box>
    </MessageWrapper>
  );
};

const UserName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '4px',
  fontSize: '0.7rem',
  color: theme.palette.primary.main,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1.1,
}));

const UserMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  lineHeight: 1.4,
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
}));

const sxRight = (theme: Theme) => ({
  alignSelf: 'flex-end',
  borderRadius: '16px 16px 4px 16px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}80, ${theme.palette.secondary.main}80)`,
  boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
});

const sxLeft = () => ({
  width: '35px',
  height: '35px',
  borderRadius: '50%',
  marginRight: '10px',
});

const MessageWrapper = styled(Box)(({ theme }) => ({
  borderRadius: '16px 16px 16px 4px',
  padding: theme.spacing(1, 1.25, 0.75, 1.25),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(76, 118, 141, 0.3)'
      : 'rgba(229, 230, 235, 0.8)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(88, 166, 198, 0.3)'
      : 'rgba(59, 130, 246, 0.18)'
  }`,
  color: theme.palette.text.primary,
  width: 'max-content',
  maxWidth: 'min(calc(100% - 50px), 480px)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: `0 6px 16px ${theme.palette.primary.main}40`,
  },
}));
